import { addDays, differenceInCalendarDays, parseISO, setHours, startOfDay, startOfToday } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { dbDate, datef } from "../dates";
import { defaultVisitTypeSchedulerConfig } from "../configurationTypes";

/**
 * UI/form model. `id` is set for persisted rows.
 */
export type CareRequirement = {
  id?: number;
  visitTypeId: string;
  serviceIds: string[];
  serviceNames: string[];
  duration: string;
  durationLocked: boolean;
  scheduleType: "timeRange" | "previousVisitRelative";
  minTime: Date;
  maxTime: Date;
  minHoursAfterLastVisit: number | null;
  maxHoursAfterLastVisit: number | null;
  rruleFreq: "daily" | "weekly";
  rruleInterval: number | null;
  rruleByweekday: string[] | null;
  dtstart: Date;
  until: Date;
  dayOfAdmission: boolean;
};

/** GraphQL mutation input shape for care requirements. */
export type CareRequirementGQL = {
  id?: number | null;
  visitTypeId: number;
  duration: number;
  rruleFreq: "daily" | "weekly";
  rruleDtstart: string;
  rruleUntil: string | null;
  rruleInterval: number | null;
  rruleByweekday: string[] | null;
  earliestStartTime: string | null;
  latestStartTime: string | null;
  minTimeFromLastVisit: number | null;
  maxTimeFromLastVisit: number | null;
  dayOfAdmission: boolean;
  serviceIds: number[];
};

export type CareRequirementUpsertGQL = Omit<CareRequirementGQL, "id"> & {
  id: number | null;
};

/** GraphQL query result shape for care requirements. */
export type CareRequirementQueryResult = {
  id: number;
  visitTypeId: number | null;
  duration: number | null;
  rruleFreq: string | null;
  rruleDtstart: string | null;
  rruleUntil: string | null;
  rruleInterval: number | null;
  rruleByweekday: string | null;
  earliestStartTime: string | null;
  latestStartTime: string | null;
  minTimeFromLastVisit: number | null;
  maxTimeFromLastVisit: number | null;
  dayOfAdmission?: boolean | null;
  services?: Array<{
    serviceId: number;
    deleted: boolean;
    service?: { name?: string } | null;
  }> | null;
};

const DEFAULT_MIN_HOURS_AFTER_LAST_VISIT = 0;
const DEFAULT_MAX_HOURS_AFTER_LAST_VISIT = 12;
const DEFAULT_EPISODE_LENGTH_OF_STAY_DAYS = 6;
const DEFAULT_DISCHARGE_OFFSET_DAYS = DEFAULT_EPISODE_LENGTH_OF_STAY_DAYS - 1;

export function parseRruleByweekdayValue(value: unknown): string[] | null {
  if (!value) {
    return null;
  }
  if (Array.isArray(value)) {
    return value.filter((d): d is string => typeof d === "string");
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter((d): d is string => typeof d === "string");
      }
    } catch {
      return null;
    }
  }
  return null;
}

export function parseServiceIdsValue(value: unknown): string[] {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === "number" ? String(v) : typeof v === "string" ? v : null))
      .filter((v): v is string => typeof v === "string" && v.length > 0);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((v) => (typeof v === "number" ? String(v) : typeof v === "string" ? v : null))
          .filter((v): v is string => typeof v === "string" && v.length > 0);
      }
    } catch {
      return [];
    }
  }
  return [];
}

export function inferCareRequirementScheduleType(fields: {
  earliestStartTime?: string | null;
  latestStartTime?: string | null;
  minTimeFromLastVisit?: number | null;
  maxTimeFromLastVisit?: number | null;
}): CareRequirement["scheduleType"] {
  const hasTimeRange =
    fields.earliestStartTime !== null && fields.earliestStartTime !== undefined
      ? true
      : fields.latestStartTime !== null && fields.latestStartTime !== undefined;
  const hasRelative =
    fields.minTimeFromLastVisit !== null && fields.minTimeFromLastVisit !== undefined
      ? true
      : fields.maxTimeFromLastVisit !== null && fields.maxTimeFromLastVisit !== undefined;
  const relativeNonDefault =
    (fields.minTimeFromLastVisit ?? 0) !== 0 || (fields.maxTimeFromLastVisit ?? 24 * 60) !== 24 * 60;

  return hasRelative && !hasTimeRange
    ? "previousVisitRelative"
    : hasTimeRange && !hasRelative
      ? "timeRange"
      : hasRelative && hasTimeRange
        ? (relativeNonDefault ? "previousVisitRelative" : "timeRange")
        : "timeRange";
}

export function parseTimeStringOnDate(
  timeStr: string | null | undefined,
  baseDate: Date,
  fallback: Date,
  timezone?: string
): Date {
  if (!timeStr) {
    return fallback;
  }

  const baseDateStr = datef(baseDate, "yyyy-MM-dd");
  const local = new Date(`${baseDateStr}T${timeStr}`);
  if (Number.isNaN(local.getTime())) {
    return fallback;
  }
  return timezone ? fromZonedTime(local, timezone) : local;
}

/**
 * "Today" as a local-midnight Date for the calendar-day convention used by
 * admission/care-requirement dates (which serialize via `dbDate` with no tz).
 * When a timezone is given, this is the patient's current calendar day — so a
 * scheduler in a different timezone still admits on the patient's local today,
 * not their own. Without a timezone it falls back to the browser's today.
 */
export function todayInTimezone(timezone?: string): Date {
  return timezone ? parseISO(dbDate(new Date(), timezone)) : startOfDay(new Date());
}

export function getDefaultCareRequirementDateRange(referenceDate?: Date, timezone?: string): {
  admissionDate: Date;
  expectedDischargeDate: Date;
} {
  const admissionDate = referenceDate ? startOfDay(referenceDate) : todayInTimezone(timezone);
  const expectedDischargeDate = startOfDay(addDays(admissionDate, DEFAULT_DISCHARGE_OFFSET_DAYS));
  return { admissionDate, expectedDischargeDate };
}

/**
 * The rrule start date for a care requirement, given its episode's admission
 * date. This is the single source of truth for the convention: day-of-admission
 * requirements run on the admission day itself; ongoing requirements start the
 * day after admission (the admission day is covered by the admit visit).
 */
export function careRequirementStartDate(admissionDate: Date, dayOfAdmission: boolean): Date {
  return dayOfAdmission ? startOfDay(admissionDate) : startOfDay(addDays(admissionDate, 1));
}

export type CareRequirementVisitTypeUsage = {
  visitTypeId: string | number | null | undefined;
};

export type CareRequirementVisitTypeScheduling = {
  id: string | number;
  name?: string | null;
  schedulingConfig?: {
    daysBeforeLastOption?: number | null;
  } | null;
};

export type LengthOfStayMaxSchedulingViolation = {
  visitTypeId: string;
  visitTypeName: string | null;
  maxSchedulingDays: number;
};

export function getLengthOfStayDays(admissionDate: Date, expectedDischargeDate: Date): number {
  return Math.max(
    0,
    differenceInCalendarDays(startOfDay(expectedDischargeDate), startOfDay(admissionDate))
  );
}

export function getLengthOfStayMaxSchedulingViolations(options: {
  admissionDate: Date;
  expectedDischargeDate: Date;
  careRequirements: CareRequirementVisitTypeUsage[];
  visitTypes: CareRequirementVisitTypeScheduling[];
}): LengthOfStayMaxSchedulingViolation[] {
  const { admissionDate, expectedDischargeDate, careRequirements, visitTypes } = options;
  const lengthOfStayDays = getLengthOfStayDays(admissionDate, expectedDischargeDate);
  const visitTypeById = new Map(visitTypes.map((visitType) => [String(visitType.id), visitType]));
  const uniqueVisitTypeIds = [...new Set(
    careRequirements
      .map((careRequirement) => careRequirement.visitTypeId)
      .filter((visitTypeId): visitTypeId is string | number => {
        if (visitTypeId === null || visitTypeId === undefined) {
          return false;
        }
        return String(visitTypeId).trim().length > 0;
      })
      .map((visitTypeId) => String(visitTypeId))
  )];

  return uniqueVisitTypeIds.flatMap((visitTypeId) => {
    const visitType = visitTypeById.get(visitTypeId);
    if (!visitType) {
      return [];
    }

    const maxSchedulingDays =
      visitType.schedulingConfig?.daysBeforeLastOption ?? defaultVisitTypeSchedulerConfig.daysBeforeLastOption;

    if (lengthOfStayDays <= maxSchedulingDays) {
      return [];
    }

    return [{
      visitTypeId,
      visitTypeName: visitType.name ?? null,
      maxSchedulingDays,
    }];
  });
}

/** Create a new UI care requirement with defaults. */
export function createNewCareRequirement(options?: {
  frequency?: "daily" | "weekly";
  admissionDate?: Date;
  expectedDischargeDate?: Date;
}): CareRequirement {
  const frequency = options?.frequency ?? "daily";
  const { admissionDate: defaultAdmissionDate, expectedDischargeDate: defaultExpectedDischargeDate } =
    getDefaultCareRequirementDateRange();
  const admissionDate = options?.admissionDate ?? defaultAdmissionDate;
  const expectedDischargeDate = options?.expectedDischargeDate ?? defaultExpectedDischargeDate;

  return {
    visitTypeId: "",
    serviceIds: [],
    serviceNames: [],
    duration: "0",
    durationLocked: false,
    scheduleType: "timeRange",
    minTime: setHours(startOfToday(), 8),
    maxTime: setHours(startOfToday(), 12),
    minHoursAfterLastVisit: DEFAULT_MIN_HOURS_AFTER_LAST_VISIT,
    maxHoursAfterLastVisit: DEFAULT_MAX_HOURS_AFTER_LAST_VISIT,
    rruleFreq: frequency,
    rruleInterval: null,
    rruleByweekday: null,
    dtstart: startOfDay(admissionDate),
    until: expectedDischargeDate,
    dayOfAdmission: false,
  };
}

/** Parse GraphQL care requirements into UI state (timezone-aware times). */
export function parseCareRequirementsFromGQL(
  careRequirements: CareRequirementQueryResult[],
  timezone?: string
): {
  careRequirements: CareRequirement[];
  admissionDate: Date;
  expectedDischargeDate: Date;
} {
  const parseStarts = (crs: CareRequirementQueryResult[]) =>
    crs
      .map((cr) => cr?.rruleDtstart)
      .filter((d): d is string => typeof d === "string")
      .map((d) => parseISO(d))
      .filter((d) => !Number.isNaN(d.getTime()));

  const dayOfStarts = parseStarts(careRequirements.filter((cr) => cr?.dayOfAdmission));
  const ongoingStarts = parseStarts(careRequirements.filter((cr) => !cr?.dayOfAdmission));

  const parsedUntils = careRequirements
    .map((cr) => cr?.rruleUntil)
    .filter((d): d is string => typeof d === "string")
    .map((d) => parseISO(d))
    .filter((d) => !Number.isNaN(d.getTime()));

  // Recover the admission date from the stored (honest) rrule starts:
  //  - a day-of-admission requirement's dtstart IS the admission date;
  //  - otherwise an ongoing requirement starts the day after admission, so
  //    admission = its dtstart − 1;
  //  - with no requirements at all, fall back to the default (today).
  const admissionDate = dayOfStarts.length
    ? startOfDay(new Date(Math.min(...dayOfStarts.map((d) => d.getTime()))))
    : ongoingStarts.length
      ? startOfDay(addDays(new Date(Math.min(...ongoingStarts.map((d) => d.getTime()))), -1))
      : getDefaultCareRequirementDateRange(undefined, timezone).admissionDate;

  const expectedDischargeDate = parsedUntils.length
    ? startOfDay(new Date(Math.max(...parsedUntils.map((d) => d.getTime()))))
    : getDefaultCareRequirementDateRange(admissionDate).expectedDischargeDate;

  const mapped: CareRequirement[] = careRequirements.map((cr) => {
    const id = cr.id;
    const services = (cr.services ?? []).filter((s) => !s.deleted);
    const serviceIds = services.map((s) => String(s.serviceId));
    const serviceNames = services.map((s) => s.service?.name ?? "");

    const scheduleType = inferCareRequirementScheduleType({
      earliestStartTime: cr.earliestStartTime,
      latestStartTime: cr.latestStartTime,
      minTimeFromLastVisit: cr.minTimeFromLastVisit,
      maxTimeFromLastVisit: cr.maxTimeFromLastVisit,
    });
    const rruleByweekday = parseRruleByweekdayValue(cr.rruleByweekday);

    return {
      id,
      visitTypeId: String(cr.visitTypeId ?? ""),
      serviceIds,
      serviceNames,
      duration: String(cr.duration ?? 0),
      durationLocked: false,
      scheduleType,
      minTime: parseTimeStringOnDate(cr.earliestStartTime, admissionDate, setHours(startOfToday(), 8), timezone),
      maxTime: parseTimeStringOnDate(cr.latestStartTime, admissionDate, setHours(startOfToday(), 12), timezone),
      minHoursAfterLastVisit: cr.minTimeFromLastVisit ? cr.minTimeFromLastVisit / 60 : null,
      maxHoursAfterLastVisit: cr.maxTimeFromLastVisit ? cr.maxTimeFromLastVisit / 60 : null,
      rruleFreq: cr.rruleFreq === "weekly" ? "weekly" : "daily",
      rruleInterval: cr.rruleInterval ?? null,
      rruleByweekday,
      dtstart: careRequirementStartDate(admissionDate, cr.dayOfAdmission ?? false),
      until: expectedDischargeDate,
      dayOfAdmission: cr.dayOfAdmission ?? false,
    };
  });

  return {
    careRequirements: mapped,
    admissionDate,
    expectedDischargeDate,
  };
}

/** Convert UI care requirement into GraphQL mutation input. */
export function unparseCareRequirementForGQL(
  careRequirement: CareRequirement,
  timezone?: string
): Omit<CareRequirementGQL, "id"> {
  const isTimeRange = careRequirement.scheduleType === "timeRange";

  return {
    visitTypeId: parseInt(careRequirement.visitTypeId, 10),
    duration: parseInt(careRequirement.duration, 10) || 0,
    rruleFreq: careRequirement.rruleFreq,
    rruleDtstart: dbDate(careRequirement.dtstart),
    rruleUntil: careRequirement.until ? dbDate(careRequirement.until) : null,
    rruleInterval:
      careRequirement.rruleFreq === "weekly"
        ? careRequirement.rruleInterval ?? 1
        : null,
    rruleByweekday:
      careRequirement.rruleFreq === "weekly"
        ? (careRequirement.rruleByweekday?.length ? careRequirement.rruleByweekday : null)
        : null,
    earliestStartTime: isTimeRange ? datef(careRequirement.minTime, "HH:mm:ss", timezone) : null,
    latestStartTime: isTimeRange ? datef(careRequirement.maxTime, "HH:mm:ss", timezone) : null,
    minTimeFromLastVisit: isTimeRange ? null : (careRequirement.minHoursAfterLastVisit ?? 0) * 60,
    maxTimeFromLastVisit: isTimeRange ? null : (careRequirement.maxHoursAfterLastVisit ?? 0) * 60,
    dayOfAdmission: careRequirement.dayOfAdmission,
    serviceIds: careRequirement.serviceIds
      .map((serviceId) => parseInt(serviceId, 10))
      .filter((serviceId) => !Number.isNaN(serviceId)),
  };
}

/** Convert UI care requirements into GraphQL mutation inputs (including ids). */
export function unparseCareRequirementsForGQL(
  careRequirements: CareRequirement[],
  timezone?: string
): CareRequirementUpsertGQL[] {
  return careRequirements.map((cr) => ({
    id: cr.id ?? null,
    ...unparseCareRequirementForGQL(cr, timezone),
  }));
}

export function cloneCareRequirement(
  careRequirement: CareRequirement,
  overrides?: Partial<CareRequirement>
): CareRequirement {
  return {
    ...careRequirement,
    id: undefined,
    serviceIds: [...careRequirement.serviceIds],
    serviceNames: [...careRequirement.serviceNames],
    rruleByweekday: careRequirement.rruleByweekday ? [...careRequirement.rruleByweekday] : null,
    minTime: new Date(careRequirement.minTime),
    maxTime: new Date(careRequirement.maxTime),
    dtstart: new Date(careRequirement.dtstart),
    until: new Date(careRequirement.until),
    ...(overrides ?? {}),
  };
}

export function withCareRequirementFrequency(
  careRequirement: CareRequirement,
  frequency: CareRequirement["rruleFreq"]
): CareRequirement {
  return {
    ...careRequirement,
    rruleFreq: frequency,
    rruleInterval: frequency === "weekly" ? careRequirement.rruleInterval ?? 1 : null,
    rruleByweekday: frequency === "weekly" ? careRequirement.rruleByweekday : null,
  };
}

export function withCareRequirementsFrequency(
  careRequirements: CareRequirement[],
  frequency: CareRequirement["rruleFreq"]
): CareRequirement[] {
  return careRequirements.map((cr) => withCareRequirementFrequency(cr, frequency));
}

export type ClinicalConstraintTemplateRequirementInput = {
  visitTypeId: number;
  duration: number;
  rruleInterval: number | null;
  rruleByweekday: string[] | null;
  earliestStartTime: string | null;
  latestStartTime: string | null;
  minTimeFromLastVisit: number | null;
  maxTimeFromLastVisit: number | null;
  serviceIds: number[];
};

export function careRequirementToClinicalConstraintTemplateRequirementInput(
  careRequirement: CareRequirement,
  timezone?: string
): ClinicalConstraintTemplateRequirementInput {
  const gql = unparseCareRequirementForGQL(careRequirement, timezone);
  return {
    visitTypeId: gql.visitTypeId,
    duration: gql.duration,
    rruleInterval: gql.rruleInterval,
    rruleByweekday: gql.rruleByweekday,
    earliestStartTime: gql.earliestStartTime,
    latestStartTime: gql.latestStartTime,
    minTimeFromLastVisit: gql.minTimeFromLastVisit,
    maxTimeFromLastVisit: gql.maxTimeFromLastVisit,
    serviceIds: gql.serviceIds,
  };
}

export type CareRequirementTemplateRequirementLike = {
  id?: number | null;
  visitTypeId?: number | null;
  duration?: number | null;
  rruleInterval?: number | null;
  rruleByweekday?: unknown;
  earliestStartTime?: string | null;
  latestStartTime?: string | null;
  minTimeFromLastVisit?: number | null;
  maxTimeFromLastVisit?: number | null;
  serviceIds?: unknown;
};

export function careRequirementFromTemplateRequirement(options: {
  requirement: CareRequirementTemplateRequirementLike;
  frequency: CareRequirement["rruleFreq"];
  admissionDate: Date;
  expectedDischargeDate: Date;
  timezone?: string;
}): CareRequirement {
  const { requirement: req, frequency, admissionDate, expectedDischargeDate, timezone } = options;

  const base = createNewCareRequirement({
    frequency,
    admissionDate,
    expectedDischargeDate,
  });

  const serviceIds = parseServiceIdsValue(req.serviceIds);
  const serviceNames: string[] = [];

  const scheduleType = inferCareRequirementScheduleType({
    earliestStartTime: req.earliestStartTime ?? null,
    latestStartTime: req.latestStartTime ?? null,
    minTimeFromLastVisit: req.minTimeFromLastVisit ?? null,
    maxTimeFromLastVisit: req.maxTimeFromLastVisit ?? null,
  });

  return {
    ...base,
    id: req.id ?? undefined,
    visitTypeId: String(req.visitTypeId ?? ""),
    serviceIds,
    serviceNames,
    duration: String(req.duration ?? 0),
    scheduleType,
    minTime: parseTimeStringOnDate(req.earliestStartTime, admissionDate, base.minTime, timezone),
    maxTime: parseTimeStringOnDate(req.latestStartTime, admissionDate, base.maxTime, timezone),
    minHoursAfterLastVisit: req.minTimeFromLastVisit ? req.minTimeFromLastVisit / 60 : null,
    maxHoursAfterLastVisit: req.maxTimeFromLastVisit ? req.maxTimeFromLastVisit / 60 : null,
    rruleFreq: frequency,
    rruleInterval: frequency === "weekly" ? req.rruleInterval ?? 1 : null,
    rruleByweekday: frequency === "weekly" ? parseRruleByweekdayValue(req.rruleByweekday) : null,
    dtstart: startOfDay(admissionDate),
    until: expectedDischargeDate,
  };
}
